/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.dev/license
 */

import {forwardRef, resolveForwardRef} from '../../di/forward_ref';
import {ÉµÉµinject, ÉµÉµinvalidFactoryDep} from '../../di/injector_compatibility';
import {ÉµÉµdefineInjectable, ÉµÉµdefineInjector} from '../../di/interface/defs';
import {registerNgModuleType} from '../../linker/ng_module_registration';
import * as iframe_attrs_validation from '../../sanitization/iframe_attrs_validation';
import * as sanitization from '../../sanitization/sanitization';
import * as r3 from '../index';

/**
 * A mapping of the @angular/core API surface used in generated expressions to the actual symbols.
 *
 * This should be kept up to date with the public exports of @angular/core.
 */
export const angularCoreEnv: {[name: string]: unknown} = (() => ({
  'ÉµÉµanimateEnter': r3.ÉµÉµanimateEnter,
  'ÉµÉµanimateEnterListener': r3.ÉµÉµanimateEnterListener,
  'ÉµÉµanimateLeave': r3.ÉµÉµanimateLeave,
  'ÉµÉµanimateLeaveListener': r3.ÉµÉµanimateLeaveListener,
  'ÉµÉµattribute': r3.ÉµÉµattribute,
  'ÉµÉµdefineComponent': r3.ÉµÉµdefineComponent,
  'ÉµÉµdefineDirective': r3.ÉµÉµdefineDirective,
  'ÉµÉµdefineInjectable': ÉµÉµdefineInjectable,
  'ÉµÉµdefineInjector': ÉµÉµdefineInjector,
  'ÉµÉµdefineNgModule': r3.ÉµÉµdefineNgModule,
  'ÉµÉµdefinePipe': r3.ÉµÉµdefinePipe,
  'ÉµÉµdirectiveInject': r3.ÉµÉµdirectiveInject,
  'ÉµÉµgetInheritedFactory': r3.ÉµÉµgetInheritedFactory,
  'ÉµÉµinject': ÉµÉµinject,
  'ÉµÉµinjectAttribute': r3.ÉµÉµinjectAttribute,
  'ÉµÉµinvalidFactory': r3.ÉµÉµinvalidFactory,
  'ÉµÉµinvalidFactoryDep': ÉµÉµinvalidFactoryDep,
  'ÉµÉµtemplateRefExtractor': r3.ÉµÉµtemplateRefExtractor,
  'ÉµÉµresetView': r3.ÉµÉµresetView,
  'ÉµÉµHostDirectivesFeature': r3.ÉµÉµHostDirectivesFeature,
  'ÉµÉµNgOnChangesFeature': r3.ÉµÉµNgOnChangesFeature,
  'ÉµÉµControlFeature': r3.ÉµÉµControlFeature,
  'ÉµÉµProvidersFeature': r3.ÉµÉµProvidersFeature,
  'ÉµÉµInheritDefinitionFeature': r3.ÉµÉµInheritDefinitionFeature,
  'ÉµÉµExternalStylesFeature': r3.ÉµÉµExternalStylesFeature,
  'ÉµÉµnextContext': r3.ÉµÉµnextContext,
  'ÉµÉµnamespaceHTML': r3.ÉµÉµnamespaceHTML,
  'ÉµÉµnamespaceMathML': r3.ÉµÉµnamespaceMathML,
  'ÉµÉµnamespaceSVG': r3.ÉµÉµnamespaceSVG,
  'ÉµÉµenableBindings': r3.ÉµÉµenableBindings,
  'ÉµÉµdisableBindings': r3.ÉµÉµdisableBindings,
  'ÉµÉµelementStart': r3.ÉµÉµelementStart,
  'ÉµÉµelementEnd': r3.ÉµÉµelementEnd,
  'ÉµÉµelement': r3.ÉµÉµelement,
  'ÉµÉµelementContainerStart': r3.ÉµÉµelementContainerStart,
  'ÉµÉµelementContainerEnd': r3.ÉµÉµelementContainerEnd,
  'ÉµÉµdomElement': r3.ÉµÉµdomElement,
  'ÉµÉµdomElementStart': r3.ÉµÉµdomElementStart,
  'ÉµÉµdomElementEnd': r3.ÉµÉµdomElementEnd,
  'ÉµÉµdomElementContainer': r3.ÉµÉµdomElementContainer,
  'ÉµÉµdomElementContainerStart': r3.ÉµÉµdomElementContainerStart,
  'ÉµÉµdomElementContainerEnd': r3.ÉµÉµdomElementContainerEnd,
  'ÉµÉµdomTemplate': r3.ÉµÉµdomTemplate,
  'ÉµÉµdomListener': r3.ÉµÉµdomListener,
  'ÉµÉµelementContainer': r3.ÉµÉµelementContainer,
  'ÉµÉµpureFunction0': r3.ÉµÉµpureFunction0,
  'ÉµÉµpureFunction1': r3.ÉµÉµpureFunction1,
  'ÉµÉµpureFunction2': r3.ÉµÉµpureFunction2,
  'ÉµÉµpureFunction3': r3.ÉµÉµpureFunction3,
  'ÉµÉµpureFunction4': r3.ÉµÉµpureFunction4,
  'ÉµÉµpureFunction5': r3.ÉµÉµpureFunction5,
  'ÉµÉµpureFunction6': r3.ÉµÉµpureFunction6,
  'ÉµÉµpureFunction7': r3.ÉµÉµpureFunction7,
  'ÉµÉµpureFunction8': r3.ÉµÉµpureFunction8,
  'ÉµÉµpureFunctionV': r3.ÉµÉµpureFunctionV,
  'ÉµÉµgetCurrentView': r3.ÉµÉµgetCurrentView,
  'ÉµÉµrestoreView': r3.ÉµÉµrestoreView,
  'ÉµÉµlistener': r3.ÉµÉµlistener,
  'ÉµÉµprojection': r3.ÉµÉµprojection,
  'ÉµÉµsyntheticHostProperty': r3.ÉµÉµsyntheticHostProperty,
  'ÉµÉµsyntheticHostListener': r3.ÉµÉµsyntheticHostListener,
  'ÉµÉµpipeBind1': r3.ÉµÉµpipeBind1,
  'ÉµÉµpipeBind2': r3.ÉµÉµpipeBind2,
  'ÉµÉµpipeBind3': r3.ÉµÉµpipeBind3,
  'ÉµÉµpipeBind4': r3.ÉµÉµpipeBind4,
  'ÉµÉµpipeBindV': r3.ÉµÉµpipeBindV,
  'ÉµÉµprojectionDef': r3.ÉµÉµprojectionDef,
  'ÉµÉµdomProperty': r3.ÉµÉµdomProperty,
  'ÉµÉµariaProperty': r3.ÉµÉµariaProperty,
  'ÉµÉµproperty': r3.ÉµÉµproperty,
  'ÉµÉµcontrol': r3.ÉµÉµcontrol,
  'ÉµÉµcontrolCreate': r3.ÉµÉµcontrolCreate,
  'ÉµÉµpipe': r3.ÉµÉµpipe,
  'ÉµÉµqueryRefresh': r3.ÉµÉµqueryRefresh,
  'ÉµÉµqueryAdvance': r3.ÉµÉµqueryAdvance,
  'ÉµÉµviewQuery': r3.ÉµÉµviewQuery,
  'ÉµÉµviewQuerySignal': r3.ÉµÉµviewQuerySignal,
  'ÉµÉµloadQuery': r3.ÉµÉµloadQuery,
  'ÉµÉµcontentQuery': r3.ÉµÉµcontentQuery,
  'ÉµÉµcontentQuerySignal': r3.ÉµÉµcontentQuerySignal,
  'ÉµÉµreference': r3.ÉµÉµreference,
  'ÉµÉµclassMap': r3.ÉµÉµclassMap,
  'ÉµÉµstyleMap': r3.ÉµÉµstyleMap,
  'ÉµÉµstyleProp': r3.ÉµÉµstyleProp,
  'ÉµÉµclassProp': r3.ÉµÉµclassProp,
  'ÉµÉµadvance': r3.ÉµÉµadvance,
  'ÉµÉµtemplate': r3.ÉµÉµtemplate,
  'ÉµÉµconditional': r3.ÉµÉµconditional,
  'ÉµÉµconditionalCreate': r3.ÉµÉµconditionalCreate,
  'ÉµÉµconditionalBranchCreate': r3.ÉµÉµconditionalBranchCreate,
  'ÉµÉµdefer': r3.ÉµÉµdefer,
  'ÉµÉµdeferWhen': r3.ÉµÉµdeferWhen,
  'ÉµÉµdeferOnIdle': r3.ÉµÉµdeferOnIdle,
  'ÉµÉµdeferOnImmediate': r3.ÉµÉµdeferOnImmediate,
  'ÉµÉµdeferOnTimer': r3.ÉµÉµdeferOnTimer,
  'ÉµÉµdeferOnHover': r3.ÉµÉµdeferOnHover,
  'ÉµÉµdeferOnInteraction': r3.ÉµÉµdeferOnInteraction,
  'ÉµÉµdeferOnViewport': r3.ÉµÉµdeferOnViewport,
  'ÉµÉµdeferPrefetchWhen': r3.ÉµÉµdeferPrefetchWhen,
  'ÉµÉµdeferPrefetchOnIdle': r3.ÉµÉµdeferPrefetchOnIdle,
  'ÉµÉµdeferPrefetchOnImmediate': r3.ÉµÉµdeferPrefetchOnImmediate,
  'ÉµÉµdeferPrefetchOnTimer': r3.ÉµÉµdeferPrefetchOnTimer,
  'ÉµÉµdeferPrefetchOnHover': r3.ÉµÉµdeferPrefetchOnHover,
  'ÉµÉµdeferPrefetchOnInteraction': r3.ÉµÉµdeferPrefetchOnInteraction,
  'ÉµÉµdeferPrefetchOnViewport': r3.ÉµÉµdeferPrefetchOnViewport,
  'ÉµÉµdeferHydrateWhen': r3.ÉµÉµdeferHydrateWhen,
  'ÉµÉµdeferHydrateNever': r3.ÉµÉµdeferHydrateNever,
  'ÉµÉµdeferHydrateOnIdle': r3.ÉµÉµdeferHydrateOnIdle,
  'ÉµÉµdeferHydrateOnImmediate': r3.ÉµÉµdeferHydrateOnImmediate,
  'ÉµÉµdeferHydrateOnTimer': r3.ÉµÉµdeferHydrateOnTimer,
  'ÉµÉµdeferHydrateOnHover': r3.ÉµÉµdeferHydrateOnHover,
  'ÉµÉµdeferHydrateOnInteraction': r3.ÉµÉµdeferHydrateOnInteraction,
  'ÉµÉµdeferHydrateOnViewport': r3.ÉµÉµdeferHydrateOnViewport,
  'ÉµÉµdeferEnableTimerScheduling': r3.ÉµÉµdeferEnableTimerScheduling,
  'ÉµÉµrepeater': r3.ÉµÉµrepeater,
  'ÉµÉµrepeaterCreate': r3.ÉµÉµrepeaterCreate,
  'ÉµÉµrepeaterTrackByIndex': r3.ÉµÉµrepeaterTrackByIndex,
  'ÉµÉµrepeaterTrackByIdentity': r3.ÉµÉµrepeaterTrackByIdentity,
  'ÉµÉµcomponentInstance': r3.ÉµÉµcomponentInstance,
  'ÉµÉµtext': r3.ÉµÉµtext,
  'ÉµÉµtextInterpolate': r3.ÉµÉµtextInterpolate,
  'ÉµÉµtextInterpolate1': r3.ÉµÉµtextInterpolate1,
  'ÉµÉµtextInterpolate2': r3.ÉµÉµtextInterpolate2,
  'ÉµÉµtextInterpolate3': r3.ÉµÉµtextInterpolate3,
  'ÉµÉµtextInterpolate4': r3.ÉµÉµtextInterpolate4,
  'ÉµÉµtextInterpolate5': r3.ÉµÉµtextInterpolate5,
  'ÉµÉµtextInterpolate6': r3.ÉµÉµtextInterpolate6,
  'ÉµÉµtextInterpolate7': r3.ÉµÉµtextInterpolate7,
  'ÉµÉµtextInterpolate8': r3.ÉµÉµtextInterpolate8,
  'ÉµÉµtextInterpolateV': r3.ÉµÉµtextInterpolateV,
  'ÉµÉµi18n': r3.ÉµÉµi18n,
  'ÉµÉµi18nAttributes': r3.ÉµÉµi18nAttributes,
  'ÉµÉµi18nExp': r3.ÉµÉµi18nExp,
  'ÉµÉµi18nStart': r3.ÉµÉµi18nStart,
  'ÉµÉµi18nEnd': r3.ÉµÉµi18nEnd,
  'ÉµÉµi18nApply': r3.ÉµÉµi18nApply,
  'ÉµÉµi18nPostprocess': r3.ÉµÉµi18nPostprocess,
  'ÉµÉµresolveWindow': r3.ÉµÉµresolveWindow,
  'ÉµÉµresolveDocument': r3.ÉµÉµresolveDocument,
  'ÉµÉµresolveBody': r3.ÉµÉµresolveBody,
  'ÉµÉµsetComponentScope': r3.ÉµÉµsetComponentScope,
  'ÉµÉµsetNgModuleScope': r3.ÉµÉµsetNgModuleScope,
  'ÉµÉµregisterNgModuleType': registerNgModuleType,
  'ÉµÉµgetComponentDepsFactory': r3.ÉµÉµgetComponentDepsFactory,
  'ÉµsetClassDebugInfo': r3.ÉµsetClassDebugInfo,
  'ÉµÉµdeclareLet': r3.ÉµÉµdeclareLet,
  'ÉµÉµstoreLet': r3.ÉµÉµstoreLet,
  'ÉµÉµarrowFunction': r3.ÉµÉµarrowFunction,
  'ÉµÉµreadContextLet': r3.ÉµÉµreadContextLet,
  'ÉµÉµattachSourceLocations': r3.ÉµÉµattachSourceLocations,
  'ÉµÉµinterpolate': r3.ÉµÉµinterpolate,
  'ÉµÉµinterpolate1': r3.ÉµÉµinterpolate1,
  'ÉµÉµinterpolate2': r3.ÉµÉµinterpolate2,
  'ÉµÉµinterpolate3': r3.ÉµÉµinterpolate3,
  'ÉµÉµinterpolate4': r3.ÉµÉµinterpolate4,
  'ÉµÉµinterpolate5': r3.ÉµÉµinterpolate5,
  'ÉµÉµinterpolate6': r3.ÉµÉµinterpolate6,
  'ÉµÉµinterpolate7': r3.ÉµÉµinterpolate7,
  'ÉµÉµinterpolate8': r3.ÉµÉµinterpolate8,
  'ÉµÉµinterpolateV': r3.ÉµÉµinterpolateV,

  'ÉµÉµsanitizeHtml': sanitization.ÉµÉµsanitizeHtml,
  'ÉµÉµsanitizeStyle': sanitization.ÉµÉµsanitizeStyle,
  'ÉµÉµsanitizeResourceUrl': sanitization.ÉµÉµsanitizeResourceUrl,
  'ÉµÉµsanitizeScript': sanitization.ÉµÉµsanitizeScript,
  'ÉµÉµvalidateAttribute': sanitization.ÉµÉµvalidateAttribute,
  'ÉµÉµsanitizeUrl': sanitization.ÉµÉµsanitizeUrl,
  'ÉµÉµsanitizeUrlOrResourceUrl': sanitization.ÉµÉµsanitizeUrlOrResourceUrl,
  'ÉµÉµtrustConstantHtml': sanitization.ÉµÉµtrustConstantHtml,
  'ÉµÉµtrustConstantResourceUrl': sanitization.ÉµÉµtrustConstantResourceUrl,

  'forwardRef': forwardRef,
  'resolveForwardRef': resolveForwardRef,

  'ÉµÉµtwoWayProperty': r3.ÉµÉµtwoWayProperty,
  'ÉµÉµtwoWayBindingSet': r3.ÉµÉµtwoWayBindingSet,
  'ÉµÉµtwoWayListener': r3.ÉµÉµtwoWayListener,

  'ÉµÉµreplaceMetadata': r3.ÉµÉµreplaceMetadata,
  'ÉµÉµgetReplaceMetadataURL': r3.ÉµÉµgetReplaceMetadataURL,
}))();
